import { memo, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { SlackMessage, SlackReaction } from '@assembly/slack'
import { QUICK_REACTIONS } from '../emoji'
import { useActions } from '../data/actions'
import { useDisplayName, useUser } from '../data/hooks'
import { useMe } from '../data/workspace'
import { useUi } from '../state/ui'
import { formatDateTime, formatTime } from '../format'
import { Composer } from './Composer'
import { EmojiPicker } from './EmojiPicker'
import { Icon } from './Icon'
import { Attachments, Blocks, Files, Mrkdwn, hasRichBlocks } from './MessageContent'
import { Emoji, IconButton, Menu, MessageAvatar, UserAvatar, anchorFrom, authorName, useLongPress, type Anchor } from './common'

const SYSTEM_SUBTYPES = new Set(['channel_join', 'channel_leave', 'channel_topic', 'channel_purpose', 'channel_name', 'channel_archive', 'channel_unarchive', 'group_join', 'group_leave', 'pinned_item'])

interface MessageProps {
  message: SlackMessage
  channel: string
  /** Hide avatar + name because the previous message has the same author. */
  grouped?: boolean
  /** Rendering inside the thread panel. */
  inThread?: boolean
  highlighted?: boolean
}

/**
 * Messages people send through an app (including Assembly) carry that app's bot_id,
 * but they're still from the person; only messages from an app itself get the badge.
 */
export function isAppMessage(message: SlackMessage, user?: { is_bot?: boolean }): boolean {
  return message.subtype === 'bot_message' || (!message.user && !!message.bot_id) || !!user?.is_bot
}

/** "You, Maya and Sam" / "Maya, Sam and 4 others": Slack's wording for who reacted. */
export function reactorsText(users: string[], count: number, me: string, name: (id: string) => string, max = 8): string {
  const names = [...users].sort((a, b) => Number(b === me) - Number(a === me)).map((u) => (u === me ? 'You' : name(u)))
  const shown = names.slice(0, max)
  const others = Math.max(count, names.length) - shown.length
  if (others > 0) return `${shown.join(', ')} and ${others} ${others === 1 ? 'other' : 'others'}`
  if (shown.length <= 1) return shown[0] ?? ''
  return `${shown.slice(0, -1).join(', ')} and ${shown.at(-1)}`
}

function ReactionPill({ reaction, channel, ts }: { reaction: SlackReaction; channel: string; ts: string }) {
  const me = useMe()
  const name = useDisplayName()
  const { toggleReaction } = useActions()
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const pill = useRef<HTMLButtonElement>(null)
  const mine = reaction.users.includes(me)
  const who = reactorsText(reaction.users, reaction.count, me, name)
  const label = `${who} reacted with :${reaction.name}:`
  const show = () => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const r = pill.current?.getBoundingClientRect()
      if (r) setTip({ x: r.left + r.width / 2, y: r.top })
    }, 250)
  }
  const hide = () => {
    clearTimeout(timer.current)
    setTip(null)
  }
  useEffect(() => () => clearTimeout(timer.current), [])
  return (
    <button
      ref={pill}
      type="button"
      className={`reaction ${mine ? 'mine' : ''}`}
      aria-label={label}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={() => toggleReaction(channel, ts, reaction.name)}
    >
      <Emoji name={reaction.name} />
      <span>{reaction.count}</span>
      {tip &&
        // Rendered at the top level so neighbouring messages can't paint over it.
        createPortal(
          <span className="reaction-tooltip" role="tooltip" style={{ left: tip.x, top: tip.y }}>
            <span className="reaction-tooltip-emoji">
              <Emoji name={reaction.name} size={32} />
            </span>
            <span>
              <strong>{who}</strong> reacted with <span className="muted-inverse">:{reaction.name}:</span>
            </span>
          </span>,
          document.body,
        )}
    </button>
  )
}

function ThreadSummary({ message, channel }: { message: SlackMessage; channel: string }) {
  const openPanel = useUi((s) => s.openPanel)
  return (
    <button type="button" className="thread-summary" onClick={() => openPanel({ type: 'thread', channel, ts: message.ts })}>
      <span className="thread-avatars">
        {(message.reply_users ?? []).slice(0, 4).map((u) => (
          <UserAvatar key={u} id={u} size={22} />
        ))}
      </span>
      <strong>
        {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
      </strong>
      {message.latest_reply && <span className="muted">Last reply {formatDateTime(message.latest_reply).replace(/^Today at /, 'today at ')}</span>}
      <Icon name="chevronRight" size={14} className="thread-chevron" />
    </button>
  )
}

function SystemMessage({ message }: { message: SlackMessage }) {
  const user = useUser(message.user)
  return (
    <div className="message message-system" data-ts={message.ts}>
      <span className="message-gutter">
        <UserAvatar id={message.user} size={20} />
      </span>
      <div className="message-body">
        <span className="muted">
          <strong>{authorName(message, user)}</strong> <Mrkdwn text={message.text.replace(/^<@[^>]+>\s*/, '')} inline />
        </span>
        <time className="muted"> {formatTime(message.ts)}</time>
      </div>
    </div>
  )
}

function MessageImpl({ message, channel, grouped, inThread, highlighted }: MessageProps) {
  const me = useMe()
  const user = useUser(message.user)
  const actions = useActions()
  const openPanel = useUi((s) => s.openPanel)
  const openModal = useUi((s) => s.openModal)
  const editing = useUi((s) => s.editing?.ts === message.ts && s.editing.channel === channel)
  const setEditing = useUi((s) => s.setEditing)
  const [picker, setPicker] = useState<Anchor | null>(null)
  const [menu, setMenu] = useState<Anchor | null>(null)
  const longPress = useLongPress(setMenu)

  if (SYSTEM_SUBTYPES.has(message.subtype ?? '')) return <SystemMessage message={message} />

  const mine = message.user === me
  const isParent = !!message.thread_ts && message.thread_ts === message.ts
  const pinned = message.pinned_to?.includes(channel)
  const showThread = !inThread && isParent && (message.reply_count ?? 0) > 0
  const richBlocks = hasRichBlocks(message.blocks)
  const isMe = message.subtype === 'me_message'

  const confirmDelete = () =>
    openModal({
      type: 'confirm',
      title: 'Delete message',
      body: 'Are you sure you want to delete this message? This can’t be undone.',
      confirm: 'Delete',
      danger: true,
      onConfirm: () => actions.deleteMessage(channel, message.ts),
    })

  const remindIn = (when: string) => () => actions.remindAboutMessage(channel, message.ts, when)

  const menuItems = [
    !inThread && { label: 'Reply in thread', icon: 'thread' as const, onSelect: () => openPanel({ type: 'thread', channel, ts: message.thread_ts ?? message.ts }) },
    { label: 'Add reaction', icon: 'smilePlus' as const, onSelect: () => setPicker(menu) },
    'separator' as const,
    { label: 'Copy link', icon: 'link' as const, onSelect: () => actions.copyLink(channel, message.ts) },
    { label: 'Copy text', icon: 'copy' as const, onSelect: () => navigator.clipboard?.writeText(message.text) },
    { label: 'Mark unread', icon: 'dot' as const, onSelect: () => actions.markUnread(channel, message.ts) },
    { label: pinned ? 'Unpin from channel' : 'Pin to channel', icon: 'pin' as const, onSelect: () => actions.togglePin(channel, message) },
    { label: 'Remind me in 1 hour', icon: 'clock' as const, onSelect: remindIn('in 1 hour') },
    { label: 'Remind me tomorrow', icon: 'calendar' as const, onSelect: remindIn('tomorrow at 9am') },
    mine && 'separator' as const,
    mine && { label: 'Edit message', icon: 'pencil' as const, onSelect: () => setEditing({ channel, ts: message.ts }) },
    mine && { label: 'Delete message', icon: 'trash' as const, danger: true, onSelect: confirmDelete },
  ]

  return (
    <div
      className={`message ${grouped ? 'grouped' : ''} ${message.pending ? 'pending' : ''} ${pinned ? 'pinned' : ''} ${highlighted ? 'highlighted' : ''} ${editing ? 'editing' : ''}`}
      data-ts={message.ts}
      {...longPress}
    >
      {pinned && (
        <div className="message-flag">
          <Icon name="pin" size={12} /> Pinned
        </div>
      )}
      {message.subtype === 'thread_broadcast' && !inThread && (
        <div className="message-flag">
          <Icon name="thread" size={12} /> Replied to a thread
        </div>
      )}
      <div className="message-row">
        <span className="message-gutter">
          {grouped ? (
            <time className="hover-time" title={formatDateTime(message.ts)}>
              {formatTime(message.ts).replace(/\s?[AP]M$/i, '')}
            </time>
          ) : (
            <button type="button" className="avatar-button" onClick={() => message.user && openPanel({ type: 'profile', user: message.user })}>
              <MessageAvatar message={message} />
            </button>
          )}
        </span>
        <div className="message-body">
          {!grouped && (
            <div className="message-header">
              <button type="button" className="author" onClick={() => message.user && openPanel({ type: 'profile', user: message.user })}>
                {authorName(message, user)}
              </button>
              {isAppMessage(message, user) && <span className="badge">App</span>}
              {user?.profile?.status_emoji && (
                <span title={user.profile.status_text}>
                  <Emoji name={user.profile.status_emoji.replace(/:/g, '')} />
                </span>
              )}
              <time title={formatDateTime(message.ts)}>{formatTime(message.ts)}</time>
            </div>
          )}

          {editing ? (
            <Composer channel={channel} editing={{ ts: message.ts, text: message.text }} onDone={() => setEditing(null)} />
          ) : (
            <>
              {richBlocks ? (
                <>
                  <Blocks blocks={message.blocks!} />
                  {message.edited && <span className="edited">(edited)</span>}
                </>
              ) : (
                (message.text || message.edited) && (
                  <div className={`message-text ${isMe ? 'me-message' : ''}`}>
                    <Mrkdwn text={message.text} inline />
                    {message.edited && <span className="edited"> (edited)</span>}
                  </div>
                )
              )}
            </>
          )}

          {message.files && message.files.length > 0 && <Files files={message.files} />}
          {message.attachments && message.attachments.length > 0 && <Attachments attachments={message.attachments} />}

          {message.reactions && message.reactions.length > 0 && (
            <div className="reactions">
              {message.reactions.map((r) => (
                <ReactionPill key={r.name} reaction={r} channel={channel} ts={message.ts} />
              ))}
              <button type="button" className="reaction add" aria-label="Add reaction" onClick={(e) => setPicker(anchorFrom(e))}>
                <Icon name="smilePlus" size={15} />
              </button>
            </div>
          )}

          {showThread && <ThreadSummary message={message} channel={channel} />}
        </div>
      </div>

      {!message.pending && !editing && (
        <div className="message-actions" onTouchStart={(e) => e.stopPropagation()}>
          {QUICK_REACTIONS.slice(0, 3).map((name) => (
            <button key={name} type="button" className="icon-button" title={`React with :${name}:`} onClick={() => actions.toggleReaction(channel, message.ts, name)}>
              <Emoji name={name} />
            </button>
          ))}
          <IconButton icon="smilePlus" label="Add reaction" onClick={(e) => setPicker(anchorFrom(e))} />
          {!inThread && <IconButton icon="thread" label="Reply in thread" onClick={() => openPanel({ type: 'thread', channel, ts: message.thread_ts ?? message.ts })} />}
          {mine && <IconButton icon="pencil" label="Edit" onClick={() => setEditing({ channel, ts: message.ts })} />}
          <IconButton icon="more" label="More actions" onClick={(e) => setMenu(anchorFrom(e))} />
        </div>
      )}

      {picker && <EmojiPicker anchor={picker} onClose={() => setPicker(null)} onSelect={(name) => actions.toggleReaction(channel, message.ts, name)} />}
      {menu && <Menu anchor={menu} onClose={() => setMenu(null)} items={menuItems} />}
    </div>
  )
}

export const Message = memo(MessageImpl)

/** Whether `message` should be visually grouped under `previous` (older). */
export function shouldGroup(message: SlackMessage, previous: SlackMessage | undefined): boolean {
  if (!previous) return false
  if (SYSTEM_SUBTYPES.has(message.subtype ?? '') || SYSTEM_SUBTYPES.has(previous.subtype ?? '')) return false
  if (message.subtype === 'thread_broadcast' || message.pinned_to?.length) return false
  const sameAuthor = (message.user ?? message.bot_id ?? message.username) === (previous.user ?? previous.bot_id ?? previous.username)
  return sameAuthor && Number(message.ts) - Number(previous.ts) < 5 * 60 && new Date(Number(message.ts) * 1000).toDateString() === new Date(Number(previous.ts) * 1000).toDateString()
}
